export const docNav = [
  { id: 'visao-geral', label: 'Descrição' },
  { id: 'fluxo', label: 'Processamento' },
  { id: 'endpoint', label: 'Requisição' },
  { id: 'entrada', label: 'Parâmetros' },
  { id: 'saida', label: 'Resposta' },
  { id: 'perfil', label: 'Objeto profile' },
  { id: 'premio', label: 'Prêmio visto' },
  { id: 'exemplos', label: 'Exemplos' },
  { id: 'erros', label: 'Erros' },
] as const

export const docTitle = 'Sessão de campanha'
export const docDescription =
  'Referência da operação que identifica o participante pelo external_id e retorna o script da roleta ou da raspadinha e o perfil na campanha.'

export const operation = {
  method: 'POST',
  path: '/api/integrations/campaigns/session',
  summary: 'Abre a sessão da campanha para um participante.',
} as const

export const flowSteps = [
  'O header X-Campaign-Token identifica a campanha.',
  'O external_id identifica o participante no sistema integrador. A consulta utiliza esse identificador.',
  'Quando o identificador já existe, a operação retorna o cadastro correspondente.',
  'Quando o identificador não existe, a operação cria o cadastro com o objeto donor.',
  'A resposta contém script_url com external_id e campaign_id, e o perfil do participante na campanha.',
]

export type FieldRequirement = 'required' | 'on-create' | 'with-donor' | 'optional'

export type DocField = {
  name: string
  place?: string
  type: string
  requirement: FieldRequirement
  description: string
}

export const requirementLabel: Record<FieldRequirement, string> = {
  required: 'Obrigatório',
  'on-create': 'Na criação',
  'with-donor': 'Com o cadastro',
  optional: 'Opcional',
}

export const inputFields: DocField[] = [
  {
    name: 'X-Campaign-Token',
    place: 'header',
    type: 'string',
    requirement: 'required',
    description: 'Token da campanha. Define se o script retornado é de roleta ou de raspadinha.',
  },
  {
    name: 'external_id',
    place: 'body',
    type: 'string',
    requirement: 'required',
    description:
      'Id do cliente no sistema integrador, exatamente como ele existe lá. 14 permanece 14, sem prefixo.',
  },
  {
    name: 'donor',
    place: 'body',
    type: 'object',
    requirement: 'on-create',
    description: 'Dados cadastrais. Exigido quando o external_id não existe.',
  },
  {
    name: 'donor.full_name',
    place: 'body',
    type: 'string',
    requirement: 'with-donor',
    description: 'Nome completo.',
  },
  {
    name: 'donor.email',
    place: 'body',
    type: 'string',
    requirement: 'with-donor',
    description: 'E-mail.',
  },
  {
    name: 'donor.phone',
    place: 'body',
    type: 'string',
    requirement: 'with-donor',
    description: 'Telefone. Somente dígitos, de 11 a 15 caracteres.',
  },
  {
    name: 'donor.document_cpf',
    place: 'body',
    type: 'string',
    requirement: 'optional',
    description: 'CPF. Somente dígitos.',
  },
]

export const outputFields: DocField[] = [
  {
    name: 'created',
    type: 'boolean',
    requirement: 'required',
    description: 'true quando o participante foi criado nesta chamada. false quando o cadastro já existia.',
  },
  {
    name: 'donor.external_id',
    type: 'string',
    requirement: 'required',
    description: 'Identificador do participante no sistema integrador.',
  },
  {
    name: 'donor.full_name',
    type: 'string',
    requirement: 'required',
    description: 'Nome do participante.',
  },
  {
    name: 'donor.email',
    type: 'string',
    requirement: 'required',
    description: 'E-mail do participante.',
  },
  {
    name: 'donor.phone',
    type: 'string',
    requirement: 'required',
    description: 'Telefone do participante.',
  },
  {
    name: 'campaign_id',
    type: 'integer',
    requirement: 'required',
    description: 'Identificador da campanha associada ao token.',
  },
  {
    name: 'game',
    type: 'string',
    requirement: 'required',
    description: 'roulette ou scratch, conforme a campanha.',
  },
  {
    name: 'script_url',
    type: 'string',
    requirement: 'required',
    description:
      'URL do script. Para roleta: https://sortee.io/roulette.js?external_id=14&campaign_id=1&cache=20261103. O mesmo jogo abre em https://sortee.io/campaign-session?external_id=14&campaign_id=1.',
  },
  {
    name: 'profile',
    type: 'object',
    requirement: 'required',
    description: 'Números, giros, prêmios e conquistas do participante na campanha.',
  },
]

export const profileFields: DocField[] = [
  {
    name: 'numbers',
    type: 'array',
    requirement: 'required',
    description: 'Números da sorte do participante na campanha.',
  },
  {
    name: 'total_numbers',
    type: 'integer',
    requirement: 'required',
    description: 'Quantidade de números.',
  },
  {
    name: 'roulette.available_spins',
    type: 'integer',
    requirement: 'required',
    description: 'Giros disponíveis.',
  },
  {
    name: 'roulette.roulette_unlocked',
    type: 'boolean',
    requirement: 'required',
    description: 'Indica se a roleta está liberada.',
  },
  {
    name: 'roulette.prizes',
    type: 'array',
    requirement: 'required',
    description: 'Prêmios obtidos na roleta.',
  },
  {
    name: 'scratch',
    type: 'object',
    requirement: 'optional',
    description: 'Estado da raspadinha quando a campanha utiliza essa mecânica.',
  },
  {
    name: 'achievements',
    type: 'array',
    requirement: 'required',
    description: 'Conquistas do participante na campanha.',
  },
]

export const prizeHistorySample = `GET /api/roulette-api/1/history?external_id=14&status=won
Authorization: Bearer rapi_SEGREDO_GUARDADO_NO_SERVIDOR`

export const scriptUrlExample =
  'https://sortee.io/roulette.js?external_id=14&campaign_id=1&cache=20261103'

export const sessionPageExample =
  'https://sortee.io/campaign-session?external_id=14&campaign_id=1'

export const scriptEmbedExample = `<script src="https://sortee.io/roulette.js?external_id=14&campaign_id=1&cache=20261103"></script>`

export const requestHeaders = `POST /api/integrations/campaigns/session
Content-Type: application/json
X-Campaign-Token: cmp_8f3a91c2`

const existingResponse = `{
  "success": true,
  "data": {
    "created": false,
    "donor": {
      "external_id": "14",
      "full_name": "Ana Souza",
      "email": "ana@email.com",
      "phone": "11987654321"
    },
    "campaign_id": 1,
    "game": "roulette",
    "script_url": "https://sortee.io/roulette.js?external_id=14&campaign_id=1&cache=20261103",
    "profile": {
      "numbers": [
        {
          "formatted_number": "000142",
          "assigned_at": "2026-10-20T14:02:00-03:00",
          "is_won": false,
          "prize_source": null
        }
      ],
      "total_numbers": 1,
      "roulette": {
        "available_spins": 2,
        "roulette_unlocked": true,
        "prizes": [
          {
            "id": 55,
            "prize_type": "coupon",
            "status": "available",
            "created_at": "2026-10-21T11:00:00-03:00"
          }
        ]
      },
      "achievements": [
        { "id": "first_purchase", "unlocked": true }
      ]
    }
  }
}`

const createdResponse = `{
  "success": true,
  "data": {
    "created": true,
    "donor": {
      "external_id": "14",
      "full_name": "Ana Souza",
      "email": "ana@email.com",
      "phone": "11987654321"
    },
    "campaign_id": 1,
    "game": "roulette",
    "script_url": "https://sortee.io/roulette.js?external_id=14&campaign_id=1&cache=20261103",
    "profile": {
      "numbers": [],
      "total_numbers": 0,
      "roulette": {
        "available_spins": 0,
        "roulette_unlocked": false,
        "prizes": []
      },
      "achievements": []
    }
  }
}`

export const examples = [
  {
    id: 'doador-existente',
    title: 'Participante existente',
    summary:
      'O cliente 14 já existe na campanha 1. A resposta devolve o script da roleta com esses dois identificadores.',
    body: `{
  "external_id": "14"
}`,
    response: existingResponse,
  },
  {
    id: 'doador-novo',
    title: 'Participante inexistente',
    summary:
      'external_id sem cadastro. A operação cria o participante com o objeto donor e retorna o script.',
    body: `{
  "external_id": "14",
  "donor": {
    "full_name": "Ana Souza",
    "email": "ana@email.com",
    "phone": "11987654321",
    "document_cpf": "12345678909"
  }
}`,
    response: createdResponse,
  },
] as const

export const errors = [
  { situation: 'Header X-Campaign-Token ausente', result: 'Entrada inválida' },
  { situation: 'Token inválido', result: 'Campanha não encontrada' },
  { situation: 'Sem external_id', result: 'Entrada inválida' },
  { situation: 'external_id novo sem dados do participante', result: 'Entrada inválida' },
  { situation: 'Cadastro incompleto na criação', result: 'Entrada inválida' },
] as const
